import { TrackedButton } from '@/app-shell/ui/tracked-button';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightBody,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightProgress,
  SpotlightTitle,
  spotlightCloseButtonStyles,
} from '@heidi/design-system/spotlight';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react';
import { type PropsWithChildren, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ROUTINES_SPOTLIGHT_TOTAL_STEPS,
  routinesSpotlightStepAtom,
  type RoutinesSpotlightStep,
} from '@heidi/agents/data';
import { useRoutinesSpotlightActions } from './useRoutinesSpotlightTour';

interface StepMedia {
  /** Path under public/images, e.g. /images/routines-spotlight/nav.png */
  src: string;
  /** Localised alt text for the still. */
  alt: string;
}

interface SpotlightStepProps extends PropsWithChildren {
  stepNumber: RoutinesSpotlightStep;
  title: ReactNode;
  description: ReactNode;
  media?: StepMedia;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /**
   * Anchor directly onto the child element (no wrapper). Use when the child is a
   * single DOM element that forwards ref and whose layout must be preserved.
   */
  anchorAsChild?: boolean;
}

/**
 * One step of the Routines spotlight tour. Wraps its anchor via `children`
 * and renders it untouched until this step is the active one.
 *
 * The active-check reads only the step atom so wrapping an anchor stays cheap —
 * queue/mutation hooks live in `ActiveSpotlightStep`, which mounts only when
 * active.
 */
function SpotlightStep(props: SpotlightStepProps) {
  const step = useAtomValue(routinesSpotlightStepAtom);

  if (step !== props.stepNumber) {
    return <>{props.children}</>;
  }

  return <ActiveSpotlightStep {...props} />;
}

function ActiveSpotlightStep({
  stepNumber,
  title,
  description,
  media,
  side = 'right',
  align = 'start',
  anchorAsChild = false,
  children,
}: SpotlightStepProps) {
  const intl = useIntl();
  const { next, close } = useRoutinesSpotlightActions();
  const isLastStep = stepNumber >= ROUTINES_SPOTLIGHT_TOTAL_STEPS;

  return (
    <Spotlight
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <SpotlightAnchor asChild={anchorAsChild}>{children}</SpotlightAnchor>
      <SpotlightContent
        side={side}
        align={align}
        sideOffset={12}
        className="z-40 max-w-[calc(100vw-2rem)]"
        data-testid={`routines-spotlight-step-${stepNumber}`}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SpotlightArrow />
        <SpotlightHeader>
          <SpotlightTitle>{title}</SpotlightTitle>
          <TrackedButton
            variant="ghost"
            size="xxs"
            iconOnly
            flush="all"
            onClick={close}
            actionTrackingName={ButtonAction.RoutinesSpotlightDismissed}
            className={spotlightCloseButtonStyles}
            aria-label={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahOGMC3' })}
          >
            <X />
          </TrackedButton>
        </SpotlightHeader>

        {media ? (
          <SpotlightBody>
            <SpotlightImage>
              {/* Product still inside a 248px card — same pattern as other spotlight media. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={media.src} alt={media.alt} />
            </SpotlightImage>
          </SpotlightBody>
        ) : null}

        <SpotlightDescription className="text-pretty">{description}</SpotlightDescription>

        <SpotlightFooter className="items-center justify-between">
          <SpotlightProgress>
            {stepNumber}/{ROUTINES_SPOTLIGHT_TOTAL_STEPS}
          </SpotlightProgress>
          {isLastStep ? (
            <TrackedButton
              variant="secondary"
              size="xs"
              onClick={close}
              actionTrackingName={ButtonAction.RoutinesSpotlightCompleted}
            >
              <FormattedMessage defaultMessage="Got it" id="NYTGIbSNz5" />
            </TrackedButton>
          ) : (
            <TrackedButton
              variant="secondary"
              size="xs"
              onClick={next}
              actionTrackingName={ButtonAction.RoutinesSpotlightNext}
            >
              <FormattedMessage defaultMessage="Next" id="9+DdtuCqLw" />
            </TrackedButton>
          )}
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
}

/** Step 1 — chat sidebar Routines entry. */
export function RoutinesNavSpotlight({ children }: PropsWithChildren) {
  const intl = useIntl();
  return (
    <SpotlightStep
      stepNumber={1}
      side="right"
      align="start"
      anchorAsChild
      title={<FormattedMessage defaultMessage="Routines live here" id="ITPVeVmqIY" />}
      description={
        <FormattedMessage
          defaultMessage="Repeatable work, from pre-charting to admin, starts in Routines."
          id="aIZfR8JdjF"
        />
      }
      media={{
        src: '/images/routines-spotlight/nav.png',
        alt: intl.formatMessage({
          defaultMessage: 'Preview of the Routines entry in the chat sidebar',
          id: 'lEzGedzDdF',
        }),
      }}
    >
      {children}
    </SpotlightStep>
  );
}

/** Step 2 — Discover browse filter. */
export function RoutinesDiscoverSpotlight({ children }: PropsWithChildren) {
  const intl = useIntl();
  return (
    <SpotlightStep
      stepNumber={2}
      side="bottom"
      align="start"
      anchorAsChild
      title={
        <FormattedMessage defaultMessage="Explore curated routines" id="cWf4kzMsj+" />
      }
      description={
        <FormattedMessage
          defaultMessage="Browse specialty-tagged routines you can add and run."
          id="s6HC7nr2NE"
        />
      }
      media={{
        src: '/images/routines-spotlight/discover.png',
        alt: intl.formatMessage({
          defaultMessage: 'Preview of the Discover routines catalog',
          id: 'jqmFoiiLrC',
        }),
      }}
    >
      {children}
    </SpotlightStep>
  );
}

/** Step 3 — Create split button. */
export function RoutinesCreateSpotlight({ children }: PropsWithChildren) {
  const intl = useIntl();
  return (
    <SpotlightStep
      stepNumber={3}
      side="bottom"
      align="end"
      title={<FormattedMessage defaultMessage="Create or add one" id="cUaHTVipyu" />}
      description={
        <FormattedMessage
          defaultMessage="Let Heidi help you create a routine, or set one up yourself"
          id="Gtl4mhaK+G"
        />
      }
      media={{
        src: '/images/routines-spotlight/create.png',
        alt: intl.formatMessage({
          defaultMessage: 'Preview of the Create control on the Routines page',
          id: '3rNEfKU3dT',
        }),
      }}
    >
      {children}
    </SpotlightStep>
  );
}

/** Step 4 — All (owned) browse filter. */
export function RoutinesMyRoutinesSpotlight({ children }: PropsWithChildren) {
  const intl = useIntl();
  return (
    <SpotlightStep
      stepNumber={4}
      side="bottom"
      align="start"
      anchorAsChild
      title={<FormattedMessage defaultMessage="My routines" id="k7xcPvGCLj" />}
      description={
        <FormattedMessage
          defaultMessage="Saved and scheduled routines land here."
          id="CIUD9mxj6Q"
        />
      }
      media={{
        src: '/images/routines-spotlight/my-routines.png',
        alt: intl.formatMessage({
          defaultMessage: 'Preview of the All routines list',
          id: 'k3r/gooac2',
        }),
      }}
    >
      {children}
    </SpotlightStep>
  );
}
